import apiClient from './api_client';

// Message types
export enum MessageStatus {
  SENT = 'SENT',
  READ = 'READ',
  NEGOTIATING = 'NEGOTIATING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED'
}

export interface Message {
  id: number;
  senderId: number;
  receiverId: number;
  content: string;
  createdAt: string;
  status: MessageStatus;
  originalPrice?: number;
  negotiatedPrice?: number;
  negotiationDetails?: string;
  droneId?: number;
}

export interface ChatUser {
  id: number;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  lastMessage: Message;
  unreadCount: number;
}

export interface NegotiationDetails {
  negotiationDetails: string;
  newPrice: number;
}

export interface AcceptResponse {
  clientSecret: string;
  orderId: number;
}

// Message service class with all API calls
class MessageService {
  /**
   * Send a new message
   */
  async sendMessage(message: Omit<Message, 'id' | 'createdAt' | 'status'>): Promise<Message> {
    return apiClient.post<Message>('/messages', message);
  }
  
  /**
   * Get all messages sent by a user
   */
  async getSentMessages(senderId: number): Promise<Message[]> {
    return apiClient.get<Message[]>(`/messages/sender/${senderId}`);
  }
  
  /**
   * Get all messages received by a user
   */
  async getReceivedMessages(receiverId: number): Promise<Message[]> {
    return apiClient.get<Message[]>(`/messages/receiver/${receiverId}`);
  }
  
  /**
   * Get conversation history between two users
   */
  async getConversation(senderId: number, receiverId: number): Promise<Message[]> {
    return apiClient.get<Message[]>(`/messages/conversation/${senderId}/${receiverId}`);
  }
  
  /**
   * Get list of users the specified user has conversations with
   */
  async getChats(userId: number): Promise<ChatUser[]> {
    return apiClient.get<ChatUser[]>(`/messages/chats/${userId}`);
  }
  
  /**
   * Delete a specific message
   */
  async deleteMessage(messageId: number): Promise<void> {
    return apiClient.delete(`/messages/${messageId}`);
  }
  
  /**
   * Update a message with negotiation details
   */
  async negotiateMessage(messageId: number, details: NegotiationDetails): Promise<Message> {
    return apiClient.post<Message>(`/messages/${messageId}/negotiate`, details);
  }
  
  /**
   * Accept a negotiated message
   */
  async acceptNegotiation(messageId: number): Promise<AcceptResponse> {
    return apiClient.post<AcceptResponse>(`/messages/${messageId}/accept`);
  }
  
  /**
   * Reject a negotiated message
   */
  async rejectNegotiation(messageId: number): Promise<Message> {
    return apiClient.post<Message>(`/messages/${messageId}/reject`);
  }
}

// Create and export a single instance
const messageService = new MessageService();
export default messageService; 